import { MessageType } from "@storytell/cp-client";
import type { Block, createStreamingChat } from "@storytell/streaming";
import { findOperationById, subscribeToBlockDeltas, getBlock } from "@storytell/streaming";
import { For, Show, createMemo, onMount, onCleanup } from "solid-js";
import { createSolidStreamingMarkdown, SolidStreamingMarkdown } from "@storytell/markdown";

interface InterstitialViewerProps {
  operationId: string;
  blockId?: string; // Optional block ID for block-specific content
  chat: ReturnType<typeof createStreamingChat>;
  title?: string;
}

/**
 * Multi-block streaming markdown renderer that handles delta events from multiple blocks
 */
const MultiBlockStreamingMarkdown = (props: { blockIds: string[] }) => {
  const markdownStreamer = createSolidStreamingMarkdown();
  const blockContentMap = new Map<string, string>();

  onMount(() => {
    // Initialize with existing content from all blocks
    for (const blockId of props.blockIds) {
      const initialBlock = getBlock(blockId)();
      if (initialBlock?.text) {
        blockContentMap.set(blockId, initialBlock.text);
        markdownStreamer.append(initialBlock.text + "\n\n");
      }
    }

    // Subscribe to delta events for all blocks
    const unsubscribers = props.blockIds.map((blockId) =>
      subscribeToBlockDeltas(blockId, (delta: string) => {
        // Track content per block and append delta
        const currentContent = blockContentMap.get(blockId) || "";
        blockContentMap.set(blockId, currentContent + delta);
        markdownStreamer.append(delta);
      }),
    );

    // Cleanup all subscriptions on unmount
    onCleanup(() => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    });
  });

  return (
    <SolidStreamingMarkdown
      doc={markdownStreamer.doc}
      options={{
        classes: {
          document: "**:animate-fade-in wrap-break-word whitespace-break-spaces",
          heading: (node) => {
            switch (node.level) {
              case 1:
                return "font-medium text-3xl mb-3 mt-4";
              case 2:
                return "font-medium text-2xl mb-3 mt-4";
              case 3:
                return "font-medium text-xl mb-3 mt-4";
              case 4:
                return "font-medium text-lg mb-3 mt-4";
              case 5:
                return "font-medium text-base mb-3 mt-4";
              case 6:
                return "font-medium text-sm mb-3 mt-4";
              default:
                return "";
            }
          },
          list: (node) => {
            switch (node.listData?.type) {
              case "ordered":
                return "list-decimal pl-4";
              case "bullet":
                return "list-disc pl-4";
              default:
                return "";
            }
          },
          paragraph: "mb-4",
          item: "mb-2",
          code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
          blockQuote: "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
          table: "w-full border-collapse",
          tableRow: "border-b border-muted-border",
          tableCell: "px-4 py-2 text-left",
          tableHead: "bg-canvas font-semibold",
          tableBody: "bg-tile",
          link: "text-brand underline",
        },
      }}
    />
  );
};

/**
 * Renders interstitial content in the extended panel using streaming markdown
 * Aggregates all interstitial blocks for the operation
 */
export const InterstitialViewer = (props: InterstitialViewerProps) => {
  // Find all interstitial blocks for this operation, or specific block if blockId provided
  const interstitialBlocks = createMemo(() => {
    const conversation = props.chat.chatState.conversation;
    const operation = findOperationById(conversation, props.operationId);

    if (!operation) return [];

    let blocks = operation.blocks.filter(
      (block) =>
        block.messageType === MessageType.INTERSTITIAL || block.messageType === MessageType.INTERSTITIAL_USER_FACING,
    );

    // If blockId is provided, filter to only that specific block
    if (props.blockId) {
      blocks = blocks.filter((block) => block.id === props.blockId);
    }

    return blocks;
  });

  const isAnyBlockProcessing = createMemo(() => {
    return interstitialBlocks().some((block) => !block.isComplete);
  });

  return (
    <div data-renderer="interstitial-viewer" class="h-full flex flex-col">
      {/* Header */}
      <div class="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center gap-3">
          <span class="text-lg">🤔</span>
          <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{props.title || "Thinking Process"}</h2>
            <p class="text-sm text-gray-600 dark:text-gray-400">Interstitial content and reasoning</p>
          </div>
          <Show when={isAnyBlockProcessing()}>
            <div class="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full flex-shrink-0" />
          </Show>
        </div>
      </div>

      {/* Content */}
      <div class="flex-1 overflow-auto p-6">
        <Show
          when={interstitialBlocks().length > 0}
          fallback={
            <div class="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <div class="text-center">
                <div class="text-sm">Waiting...</div>
              </div>
            </div>
          }
        >
          <div class="prose prose-sm dark:prose-invert max-w-none">
            <MultiBlockStreamingMarkdown blockIds={interstitialBlocks().map((block) => block.id)} />
          </div>
        </Show>
      </div>

      {/* Footer with block count */}
      <div class="flex-shrink-0 px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div class="text-xs text-gray-600 dark:text-gray-400">
          {interstitialBlocks().length} interstitial block{interstitialBlocks().length !== 1 ? "s" : ""}
        </div>
      </div>
    </div>
  );
};
